// src/components/module/compras/ViewCompraModal.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Stack,
  Fade,
  alpha,
  Divider,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { ICompra, IDetalleCompleto } from "../../types/compras.types";
import comprasApi from "../../../services/api/comprasApi";
import { DarkModeContext } from "../../../context/darkModeContext";

// Paleta Notion/Linear
const P = {
  light: {
    bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F",
    textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7",
    accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C",
    successBg: "#DBEDDB", warning: "#D9730D", warningBg: "#FADEC9",
    error: "#E03E3E", errorBg: "#FBE4E4",
  },
  dark: {
    bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6",
    textS: "#9B9A97", textT: "#6B6B6B", border: "#333333",
    accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A",
    successBg: "#1A2F2A", warning: "#C77D48", warningBg: "#2E2215",
    error: "#D44C47", errorBg: "#3D2020",
  },
};

const getEstadoStyle = (estado: string, dark: boolean) => {
  const p = dark ? P.dark : P.light;
  const s = estado?.toLowerCase() || "";
  if (s.includes("finalizada")) return { bg: p.successBg, color: p.success, dot: p.success };
  if (s.includes("cancelada")) return { bg: p.errorBg, color: p.error, dot: p.error };
  if (s.includes("pendiente")) return { bg: p.warningBg, color: p.warning, dot: p.warning };
  return { bg: p.accentBg, color: p.accent, dot: p.accent };
};

interface ViewCompraModalProps {
  open: boolean;
  onClose: () => void;
  compra: ICompra | null;
}

const ViewCompraModal: React.FC<ViewCompraModalProps> = ({ open, onClose, compra }) => {
  const [detalle, setDetalle] = useState<IDetalleCompleto | null>(null);
  const [loading, setLoading] = useState(false);
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  useEffect(() => {
    if (open && compra?.Id) {
      setLoading(true);
      comprasApi.getDetalleCompleto(compra.Id)
        .then(setDetalle)
        .catch(() => setDetalle(null))
        .finally(() => setLoading(false));
    }
  }, [open, compra?.Id]);

  if (!open) return null;

  const estadoStyle = getEstadoStyle(detalle?.compra?.estadoCompra?.Nombre || "", darkMode);
  const fmt = (d: string | undefined) => {
    if (!d) return "—";
    const date = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T12:00:00') : new Date(d);
    return format(date, "dd MMM yyyy", { locale: es });
  };
  const money = (v: any) => `$${Number(v || 0).toLocaleString("es-CO", { minimumFractionDigits: 0 })}`;

  return (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "14px",
            border: `1px solid ${p.border}`,
            bgcolor: p.bg,
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 3, py: 2.5,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          borderBottom: `1px solid ${p.border}`,
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "10px", bgcolor: p.accentBg,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "1rem", fontWeight: 700, color: p.accent,
              fontFamily: "'IBM Plex Mono', monospace",
            }}>
              OC
            </Box>
            <Box>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: p.text, letterSpacing: "-0.01em" }}>
                Orden de Compra #{compra?.OrdenCompra || "—"}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: p.textT }}>
                Detalle completo de la orden
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{
            width: 32, height: 32, borderRadius: "8px",
            color: p.textT, "&:hover": { bgcolor: p.bgT },
          }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, bgcolor: p.bgS }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={6}>
              <CircularProgress size={28} sx={{ color: p.accent }} />
            </Box>
          ) : detalle ? (
            <Fade in timeout={300}>
              <Box>
                {/* Info cards */}
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
                  gap: 2, mb: 3,
                }}>
                  {[
                    { label: "Estado", value: (
                      <Box sx={{
                        display: "inline-flex", alignItems: "center", gap: 0.75,
                        px: 1.5, py: 0.4, borderRadius: "6px",
                        bgcolor: estadoStyle.bg, color: estadoStyle.color,
                        fontSize: "0.82rem", fontWeight: 600,
                      }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: estadoStyle.dot }} />
                        {detalle.compra?.estadoCompra?.Nombre || "—"}
                      </Box>
                    )},
                    { label: "Proveedor", value: detalle.compra?.proveedor?.Nombre || "—" },
                    { label: "F. Solicitud", value: fmt(detalle.compra?.FechaSolicitud) },
                    { label: "F. Entrega", value: fmt(detalle.compra?.FechaEntrega) },
                  ].map((item, i) => (
                    <Paper key={i} elevation={0} sx={{
                      p: 2, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg,
                    }}>
                      <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>
                        {item.label}
                      </Typography>
                      {typeof item.value === "string" ? (
                        <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: p.text }}>{item.value}</Typography>
                      ) : item.value}
                    </Paper>
                  ))}
                </Box>

                {/* Artículos */}
                <Paper elevation={0} sx={{
                  borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg, overflow: "hidden", mb: 3,
                }}>
                  <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${p.border}` }}>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 650, color: p.text }}>
                      Artículos ({detalle.articulosCompra?.length || 0})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: p.bgS }}>
                          {["EAN", "Artículo", "Sucursal", "Cant.", "Precio Unit.", "Total", "Stock"].map((h) => (
                            <TableCell key={h} sx={{
                              fontSize: "0.72rem", fontWeight: 600, color: p.textT,
                              textTransform: "uppercase", letterSpacing: "0.04em",
                              borderBottom: `1px solid ${p.border}`, py: 1,
                            }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detalle.articulosCompra?.map((art, i) => (
                          <TableRow key={i} sx={{
                            "&:hover": { bgcolor: darkMode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)" },
                            "& td": { borderBottom: `1px solid ${p.border}`, py: 1.2, fontSize: "0.84rem" },
                          }}>
                            <TableCell sx={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "0.8rem", color: p.textS }}>
                              {art.articulo?.EAN || "—"}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 500, color: p.text }}>{art.articulo?.Nombre || "—"}</TableCell>
                            <TableCell sx={{ color: p.textS }}>{art.sucursal?.Nombre || "—"}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{art.CantidadComprada}</TableCell>
                            <TableCell sx={{ fontFamily: "'IBM Plex Mono', monospace" }}>{money(art.PrecioCompra)}</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>
                              {money(art.CantidadComprada * Number(art.PrecioCompra))}
                            </TableCell>
                            <TableCell>{art.StockActual ?? "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                {/* Detalle de pago */}
                {detalle.detalleCompra && (
                  <Paper elevation={0} sx={{
                    borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg, overflow: "hidden",
                  }}>
                    <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${p.border}` }}>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 650, color: p.text }}>
                        Detalle de Pago
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2.5 }}>
                      <Box sx={{
                        display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2, mb: 2,
                      }}>
                        <Box>
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", mb: 0.3 }}>Factura</Typography>
                          <Typography sx={{ fontSize: "0.88rem", fontWeight: 500, color: p.text }}>{detalle.detalleCompra.NumeroFactura || "—"}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", mb: 0.3 }}>Fecha Recibo</Typography>
                          <Typography sx={{ fontSize: "0.88rem", color: p.text }}>{fmt(detalle.detalleCompra.FechaRecibo)}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", mb: 0.3 }}>Observación</Typography>
                          <Typography sx={{ fontSize: "0.88rem", color: p.textS }}>{detalle.detalleCompra.Observacion || "Sin observaciones"}</Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ borderColor: p.border, my: 2 }} />

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase" }}>Total</Typography>
                          <Typography sx={{ fontSize: "1.3rem", fontWeight: 700, color: p.text, fontFamily: "'IBM Plex Mono', monospace" }}>
                            {money(detalle.detalleCompra.Total)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase" }}>Abono</Typography>
                          <Typography sx={{ fontSize: "1.3rem", fontWeight: 700, color: p.success, fontFamily: "'IBM Plex Mono', monospace" }}>
                            {money(detalle.detalleCompra.Abono)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase" }}>Saldo</Typography>
                          <Typography sx={{ fontSize: "1.3rem", fontWeight: 700, color: detalle.detalleCompra.Saldo > 0 ? p.error : p.success, fontFamily: "'IBM Plex Mono', monospace" }}>
                            {money(detalle.detalleCompra.Saldo)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Paper>
                )}
              </Box>
            </Fade>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography sx={{ color: p.textS }}>No se pudo cargar la información.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
  );
};

export default ViewCompraModal;
